import React, { useCallback, useState } from 'react';

import { max, min } from 'd3-array';
import { scaleLinear, scaleTime } from 'd3-scale';

import flatten from 'lodash/flatten';

import addMilliseconds from 'date-fns/addMilliseconds';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds'
import subMilliseconds from 'date-fns/subMilliseconds';

import COLORS from '../../../../constants/colors.constants';

import {
  CURVE_TYPE,
  convertMousePointToAxisPoint,
  getClosestTimeSeriesPoint,
  getDistanceBetweenPointsByYOnly,
} from '../d3-helpers';

import Area from '../line/D3Area';
import ChartGrid from '../axis/D3ChartGrid';
import ClipSection from '../utility/D3ClipPath';
import Line from '../line/D3Line';
import MouseoverDetection from '../utility/D3MouseoverDetection';
import MouseoverTooltip from '../utility/D3MouseoverTooltip';
import Points from './D3Points';
import TimeRangeAxis from '../axis/D3TimeRangeAxis';

const getAllXValues = (datasets = []) => flatten(datasets.map(({ data }) => data.map(({ x }) => x)))
const getAllYValues = (datasets = []) => flatten(datasets.map(({ data }) => data.map(({ y }) => y)))
const getAllDatapoints = (datasets = []) => flatten(datasets.map(dataset => dataset.data.map(item => ({ ...item, color: dataset.lineColor }))));

const ThresholdLine = ({ 
  id, 
  color, 
  xScale, 
  yScale,
  startDate, 
  endDate, 
  errorThreshold
}) => {
  const data = [
    {
      x: startDate,
      y: errorThreshold,
    },
    {
      x: endDate,
      y: errorThreshold,
    }
  ]
  return <Line
    id={`${id}-line`}
    data={data}
    color={color}
    xScale={xScale}
    yScale={yScale}
    curve={CURVE_TYPE.STEP}
  />
}

const DEFAULT_MARGINS = {
  top: 32,
  right: 32,
  bottom: 104,
  left: 40,
};

const getStepSizeForRangeAndTicks = (startDate, endDate, numberOfTicks) => {
  const diffBetweenDates = differenceInMilliseconds(endDate, startDate);
  return numberOfTicks ? diffBetweenDates / numberOfTicks : diffBetweenDates / 4;
}

export const D3PointChart = ({
  datasets,
  numberOfXTicks,
  numberOfYTicks,
  xAxisLabel,
  yAxisLabel,
  xTickFormat,
  yTickFormat,
  renderTooltip,
  yAxisPadding = 1.2,
  chartId = 'point-chart',
  margins = DEFAULT_MARGINS,
  errorThreshold = 0,
  radius = 4,
  onPointClick = () => {},
  showArea = false,
  showPoints = true,
  showGrid = true,
  useTickWrapping = false,
  containerWidth = 800,
  containerHeight = 400,
  tooltipPointTolerance = 1,
  labelRotation = -15,
  animateHover = false,
}) => {
  const clipPathId = `${chartId}-clip-path`;
  const [tooltipPoint, setTooltipPoint] = useState(null);

  const width = containerWidth > 0 ? containerWidth - margins.left - margins.right : 0;
  const height = containerHeight > 0 ? containerHeight - margins.top - margins.bottom : 0;

  const allXValues = getAllXValues(datasets);
  const allYValues = getAllYValues(datasets);

  const startDate = min(allXValues);
  const endDate = max(allXValues);
  const stepSize = getStepSizeForRangeAndTicks(startDate, endDate, numberOfXTicks)
  const paddedStartDate = subMilliseconds(startDate, stepSize);
  const paddedEndDate = addMilliseconds(endDate, stepSize);

  const maxY = max([...allYValues, errorThreshold]);
  const paddedMaxY = ((Math.round(maxY / 10) * 10) || maxY) * yAxisPadding;

  const xScale = scaleTime()
    .domain([paddedStartDate, paddedEndDate])
    .range([0, width]);

  const yScale = scaleLinear()
    .domain([0, paddedMaxY])
    .range([height, 0])
    .nice();

  const getClosestPoint = useCallback((mousePosition) => {
    if (mousePosition) {
      const clickedPoint = convertMousePointToAxisPoint(xScale, yScale, mousePosition);

      if (clickedPoint?.x && clickedPoint?.y) {
        const allData = getAllDatapoints(datasets).map((item, index) => ({ ...item, index }));
        const closestPoint = getClosestTimeSeriesPoint(allData, clickedPoint);

        // ENG-5007 - Undefined values being found in chart as of recent update - this prevents crashes
        if (!closestPoint) {
          return null;
        }
        const distanceFromClosest = getDistanceBetweenPointsByYOnly(closestPoint, clickedPoint?.y);

        return distanceFromClosest <= tooltipPointTolerance ? closestPoint : null;
      }

      return null;
    }

    return null;
  }, [datasets, tooltipPointTolerance, xScale, yScale])

  const onMouseHover = useCallback((mousePosition) => {
    if (!mousePosition) {
      return null;
    }
    const clickedPoint = convertMousePointToAxisPoint(xScale, yScale, mousePosition);
    const closestPoint = getClosestPoint(mousePosition);
    if (!closestPoint) {
      setTooltipPoint(null);
    } else {
      const distanceFromClosest = getDistanceBetweenPointsByYOnly(closestPoint, clickedPoint?.y);

      if (distanceFromClosest <= tooltipPointTolerance) {
        setTooltipPoint(closestPoint)
      } else {
        setTooltipPoint(null);
      }
    }
  }, [getClosestPoint, tooltipPointTolerance, xScale, yScale])

  const handlePointClick = useCallback((mousePosition) => {
    const closestPoint = getClosestPoint(mousePosition);
    if (closestPoint) {
      onPointClick(closestPoint)
    }
  }, [getClosestPoint, onPointClick])

  return (
    <>
      <svg
        id={chartId}
        className="pointChartSvg"
        width={width + margins.left + margins.right}
        height={height + margins.top + margins.bottom}
      >
        <ClipSection
          id={clipPathId}
          x={0}
          y={0}
          height={height}
          width={width}
        />
        <g
          transform={`translate(${margins.left}, ${margins.top})`}
        >
          {
            showGrid && (
              <ChartGrid
                chartId={chartId}
                clipPathId={clipPathId}
                xScale={xScale}
                yScale={yScale}
                height={height}
                width={width}
                xTicks={numberOfXTicks}
                yTicks={numberOfYTicks}
                xTickValues={allXValues}
                // yTickValues={yTickValues}
              />
            )
          }
          <TimeRangeAxis
            chartId={chartId}
            clipPathId={clipPathId}
            xScale={xScale}
            yScale={yScale}
            height={height}
            width={width}
            margins={margins}
            xTickFormat={xTickFormat}
            yTickFormat={yTickFormat}
            xTicks={numberOfXTicks}
            yTicks={numberOfYTicks}
            xTickValues={allXValues}
            // yTickValues={yTickValues}
            xAxisLabel={xAxisLabel}
            yAxisLabel={yAxisLabel}
            useTickWrapping={useTickWrapping}
            labelRotation={labelRotation}
          />
          <g clipPath={`url(#${clipPathId})`}>
            {
              datasets?.length && (
                datasets.map(dataset => (
                  <g key={`data-section-${dataset.id}`} id={`data-section-${dataset.id}`}>
                    {
                      showArea && (
                        <Area
                          id={`${chartId}-${dataset.id}-area`}
                          xScale={xScale}
                          yScale={yScale}
                          data={dataset.data}
                          curve={CURVE_TYPE.MONOTONE}
                          fill={COLORS.border.darker}
                        />
                      )
                    }
                    {
                      showPoints && (
                        <Points
                          id={`${chartId}-${dataset.id}-points`}
                          xScale={xScale}
                          yScale={yScale}
                          points={dataset.data}
                          selectedPoint={tooltipPoint}
                          radius={radius}
                          animateHover={animateHover}
                        />
                      )
                    }
                  </g>
                ))
              )
            }
            {
              errorThreshold && (
                <ThresholdLine
                  id={`error-threshold-${chartId}`}
                  color={COLORS.error}
                  startDate={paddedStartDate}
                  endDate={paddedEndDate}
                  xScale={xScale}
                  yScale={yScale}
                  errorThreshold={errorThreshold}
                />
              )
            }
          </g>
          {
            showPoints && (
              <MouseoverDetection
                x={0}
                y={0}
                width={width}
                height={height}
                onClick={handlePointClick}
                onMouseHover={onMouseHover}
                hidePointer={!tooltipPoint}
              />
            )
          }
        </g>
      </svg>
      <MouseoverTooltip
        id={`${chartId}-tooltip`}
        xScale={xScale}
        yScale={yScale}
        tooltipPoint={tooltipPoint}
        hideZeroValues
      >
        { renderTooltip && renderTooltip(tooltipPoint) }
      </MouseoverTooltip>
    </>
  )
};

export default D3PointChart;
